/**
 * NotesBlockControls — the "+" and drag handle rendered in the block gutter
 * (absolutely positioned, appearing on hover). Minimal presentational logic.
 */
import { useRef } from 'react'
import { Plus, GripVertical } from 'lucide-react'
import { SlashMenu, type BlockType } from './SlashMenu'
import { useState } from 'react'

interface Props {
  onInsert: (type: BlockType) => void
  /** Drag props from useBlockReorder.makeDraggableProps. */
  draggableProps: React.HTMLAttributes<HTMLElement> & { draggable?: boolean }
  /** Keyboard-reorder props from useBlockReorder.makeKeyboardReorderProps. */
  keyboardReorderProps?: React.HTMLAttributes<HTMLElement> & { tabIndex: number }
}

export function NotesBlockControls({ onInsert, draggableProps, keyboardReorderProps }: Props) {
  const [slashAt, setSlashAt] = useState<{ top: number; left: number } | null>(null)
  const plusRef = useRef<HTMLButtonElement>(null)

  return (
    <div
      className="block-controls absolute flex items-center transition-opacity"
      style={{
        left: -44, top: 0,
        gap: 0,
        zIndex: 2,
      }}
    >
      {/* Persistent + : visible at low opacity by default, full opacity on group-hover */}
      <button
        ref={plusRef}
        type="button"
        tabIndex={-1}
        title="Insert block below"
        aria-label="Insert block below"
        onMouseDown={(e) => e.preventDefault()}
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          const el = e.currentTarget
          const r = el.getBoundingClientRect()
          setSlashAt({ top: r.bottom + window.scrollY + 4, left: r.left + window.scrollX })
        }}
        className="flex items-center justify-center rounded transition-all opacity-30 group-hover:opacity-100 hover:bg-warm-100"
        style={{ width: 22, height: 22, color: 'var(--color-fg-muted)', background: 'transparent', border: 0, padding: 0 }}
      >
        <Plus size={14} />
      </button>
      {/* Grip handle: hover-only, doesn't compete when reading */}
      <span
        {...draggableProps}
        {...(keyboardReorderProps ?? {})}
        title="Drag to reorder. Alt+↑/↓ with keyboard."
        className="flex items-center justify-center rounded opacity-0 group-hover:opacity-100 transition-opacity hover:bg-warm-100 focus:outline-none focus:ring-2 focus:ring-business focus:ring-offset-1"
        style={{ width: 22, height: 22, color: 'var(--color-fg-muted)', cursor: 'grab', userSelect: 'none' }}
      >
        <GripVertical size={12} />
      </span>
      {slashAt && (
        <SlashMenu
          anchor={slashAt}
          onPick={(t) => { setSlashAt(null); onInsert(t) }}
          onClose={() => setSlashAt(null)}
        />
      )}
    </div>
  )
}
